/**
 * InsightsSummary Component
 * Displays project summary and key metrics.
 */

import React from 'react';
import {
  FileText,
  Lightbulb,
  HelpCircle,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import type { AnalysisMetrics } from '@/services/projectAnalysisApi';

interface InsightsSummaryProps {
  summary: string;
  metrics: AnalysisMetrics;
}

export const InsightsSummary: React.FC<InsightsSummaryProps> = ({
  summary,
  metrics,
}) => {
  const statCards = [
    {
      label: 'Documents',
      value: metrics.totalDocuments,
      icon: <FileText className="w-5 h-5" />,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      label: 'Decisions',
      value: metrics.decisionsCount,
      icon: <Lightbulb className="w-5 h-5" />,
      color: 'bg-purple-100 text-purple-600',
    },
    {
      label: 'Questions',
      value: metrics.openQuestionsCount,
      icon: <HelpCircle className="w-5 h-5" />,
      color: 'bg-amber-100 text-amber-600',
    },
    {
      label: 'Risks',
      value: metrics.risksCount,
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'bg-red-100 text-red-600',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
      {/* Summary */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          Project Overview
        </h2>
        <p className="text-slate-700 leading-relaxed">
          {summary || 'No summary available. Run an analysis to generate insights.'}
        </p>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-3 p-3 rounded-xl bg-slate-50"
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
              {stat.icon}
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
              <div className="text-xs text-slate-500">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      {Object.keys(metrics.documentsByCategory).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs text-slate-500 mb-2">Documents by category</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(metrics.documentsByCategory).map(([category, count]) => (
              <span
                key={category}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs capitalize"
              >
                {category}: {count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsSummary;
